import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import CompanyApi from '../company/companyApi';
import { Company } from './companyType';
import { fetchLineOfBusiness } from '../reference/referenceApi';
import { US_STATES } from '../reference/referenceData';
import ExistingCompanyView from './existingCompanyView';
import CreateCompanyForm from './createCompanyForm';
import { getSessionData } from '../lib/global_function';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import CTASection from '../home/sections/CTA';

const CreateCompanyPage: React.FC = () => {
  const [existingCompany, setExistingCompany] = useState<Company | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const navigate = useNavigate();
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [company, setCompany] = useState<Partial<Company>>({
    name: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip: '',
    line_of_business: '',
    phone: '',
    email: '',
    web_url: '',
  });
  const [isCreateSuccess, setIsCreateSuccess] = useState(false);

  const stateOptions = Object.values(US_STATES);

  useEffect(() => {
    const fetchCompany = async () => {
      setErrorMessage('');
      const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
      const companyCode = sessionData?.company_code;

      if (companyCode?.toString().length === 6) {
        try {
          const company = await CompanyApi.getCompanyByCode(companyCode);
          setExistingCompany(company);
        } catch (error) {
          if (error instanceof Error) {
            throw new Error(`Failed to get company: ${error.message}`);
          } else {
            throw new Error('Failed to get company: An unknown error occurred.');
          }
        }
      }
    };
    fetchCompany();
  }, []);

  useEffect(() => {
    const loadLineOfBusiness = async () => {
      const lineOfBusinessData = await fetchLineOfBusiness();
      setLineOfBusinessOptions(lineOfBusinessData);
    };
    loadLineOfBusiness();
  }, []);

  const handleCreateCompany = async (companyData: Partial<Company>) => {
    setErrorMessage('');

    try {
      const response = await CompanyApi.create(companyData as Company);
      const companySession = {
        company_code: response.data.attributes.company_code,
        company_name: response.data.attributes.name,
      };

      sessionStorage.setItem(
        process.env.REACT_APP_SESSION_STORAGE_KEY as string,
        btoa(JSON.stringify(companySession))
      );

      setIsCreateSuccess(true);
    } catch (error) {
      setIsCreateSuccess(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleCreateAccount = () => {
    navigate('/create_account');
  };

  return (
    <>
      <section className="page-padding">
        <div className="container-safe">
          <div className="mx-auto max-w-3xl">
            {/* Error Message */}
            {errorMessage && (
              <Alert variant="error" className="mb-6">
                {errorMessage}
              </Alert>
            )}

            {/* Success View */}
            {isCreateSuccess ? (
              <div className="space-y-6">
                <Alert variant="success">
                  <div className="space-y-3">
                    <h2 className="text-xl font-semibold">Company Created Successfully!</h2>
                    <p className="text-base">Your company has been created.</p>
                    <p className="text-sm">
                      Step 2 is to create an account. Please click the button below to continue.
                    </p>
                  </div>
                </Alert>

                <div >
                  <Button onClick={handleCreateAccount} size="medium" variant="default">
                    <UserPlus className="size-5" />
                    Create Online Account
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {existingCompany ? (
                  <ExistingCompanyView
                    company={existingCompany}
                    onCreateAccount={handleCreateAccount}
                  />
                ) : (
                  <CreateCompanyForm
                    initialCompany={company}
                    lineOfBusinessOptions={lineOfBusinessOptions}
                    stateOptions={stateOptions}
                    onSubmit={handleCreateCompany}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
};

export default CreateCompanyPage;